(() => {
  'use strict';

  const groups = [
    ['hero', 'Overview'],
    ['value_proposition', 'Key benefits'],
    ['features', 'Features'],
    ['capabilities', 'Capabilities'],
    ['installation', 'Installation & Support'],
    ['specifications', 'Specifications'],
    ['package', 'What’s in the box'],
    ['maintenance', 'Use & Care'],
    ['faq', 'FAQ'],
    ['trust', 'Trust'],
    ['scenarios', 'Scenarios'],
    ['final_cta', 'Next steps']
  ];

  const meaningful = (value) =>
    typeof value === 'string' &&
    value.trim() &&
    !/require confirmation|needs? (?:real |verified )?(?:data|evidence)|BLOCKED_MISSING|not yet available/i.test(value);

  const el = (tag, text) => {
    const element = document.createElement(tag);
    if (text !== undefined && text !== null) element.textContent = text;
    return element;
  };

  function clean(html, media) {
    const template = document.createElement('template');
    template.innerHTML = html || '';
    template.content
      .querySelectorAll('script, style, iframe, form, input, button, object, embed')
      .forEach((node) => node.remove());

    template.content.querySelectorAll('*').forEach((node) => {
      for (const attribute of [...node.attributes]) {
        if (!['href', 'src', 'alt', 'width', 'height', 'colspan', 'rowspan'].includes(attribute.name)) {
          node.removeAttribute(attribute.name);
        }
      }

      for (const attribute of ['href', 'src']) {
        if (!node.hasAttribute(attribute)) continue;
        const value = node.getAttribute(attribute) || '';
        if (!/^(?:https?:\/\/|\/|#|mailto:|tel:)/i.test(value)) node.removeAttribute(attribute);
      }

      if (node.tagName === 'IMG') {
        const mapped = media?.[node.getAttribute('src')];
        if (mapped?.url) {
          node.src = mapped.url;
          node.width = mapped.width || '';
          node.height = mapped.height || '';
        }
        node.loading = node.loading || 'lazy';
        node.decoding = 'async';
        if (!node.getAttribute('src')) node.remove();
      }
    });

    return template.content;
  }

  function appendRich(parent, value, media) {
    if (!meaningful(value)) return false;
    const fragment = clean(value, media);
    if (!fragment.textContent.trim() && !fragment.querySelector('img, video')) return false;
    parent.append(fragment);
    return true;
  }

  function appendMedia(parent, item) {
    if (meaningful(item?.video)) {
      const video = el('video');
      video.controls = true;
      video.playsInline = true;
      video.preload = 'metadata';
      if (item.poster) video.poster = item.poster;
      const source = el('source');
      source.src = item.video;
      if (item.video_type) source.type = item.video_type;
      video.append(source);
      parent.append(video);
      return true;
    }

    if (meaningful(item?.image)) {
      const image = el('img');
      image.src = item.image;
      image.alt = item.alt || '';
      image.loading = 'lazy';
      image.decoding = 'async';
      if (item.link) {
        const link = el('a');
        link.href = item.link;
        link.append(image);
        parent.append(link);
      } else {
        parent.append(image);
      }
      return true;
    }

    return false;
  }

  function renderItems(section, key, items, media) {
    let count = 0;
    for (const item of items || []) {
      const title = item?.title || item?.label || item?.question;
      const body = item?.body || item?.answer || item?.description;
      const value = item?.value;
      if (!meaningful(title) && !meaningful(body) && !meaningful(value)) continue;

      if (key === 'faq' || (meaningful(title) && meaningful(body) && !meaningful(value))) {
        const details = el('details');
        if (meaningful(title)) details.append(el('summary', title));
        const panel = el('div');
        panel.className = 'xlf-panel';
        if (appendRich(panel, body, media)) details.append(panel);
        else if (meaningful(value)) details.append(el('div', value));
        section.append(details);
      } else {
        const row = el('div');
        row.className = 'xlf-item';
        if (meaningful(title)) row.append(el('h3', title));
        if (meaningful(value)) row.append(el('p', value));
        if (meaningful(body)) appendRich(row, body, media);
        section.append(row);
      }
      count += 1;
    }
    return count;
  }

  function render(root, data, media = {}) {
    root.replaceChildren();
    const details = data.details || {};

    const section = (key, title) => {
      const node = el('section');
      node.dataset.lowerModule = key;
      if (meaningful(title)) node.append(el('h2', title));
      return node;
    };

    for (const [key, defaultTitle] of groups) {
      const group = details[key];
      if (!group) continue;
      const items = Array.isArray(group) ? group : group.items || [];
      const node = section(key, group.heading || group.title || defaultTitle);
      let added = false;

      if (key === 'hero' && meaningful(group.subheading)) {
        node.append(el('p', group.subheading));
        added = true;
      }
      if (meaningful(group.body)) added = appendRich(node, group.body, media) || added;
      if (group.image || group.video) added = appendMedia(node, group) || added;

      if (key === 'specifications') {
        const list = el('dl');
        for (const item of items) {
          if (meaningful(item?.title || item?.label) && meaningful(item?.value)) {
            list.append(el('dt', item.title || item.label), el('dd', item.value));
            added = true;
          }
        }
        if (added) node.append(list);
      } else if (renderItems(node, key, items, media)) {
        added = true;
      }

      if (key === 'final_cta' && meaningful(group.button_label) && meaningful(group.button_url)) {
        const link = el('a', group.button_label);
        link.href = group.button_url;
        link.className = 'xuk-pdp-cta-link';
        node.append(link);
        added = true;
      }

      if (added) root.append(node);
    }

    for (const block of data.blocks || []) {
      if (!block || block.visible === false) continue;
      const node = section(`block-${block.id || 'custom'}`, block.heading);
      let added = appendRich(node, block.body, media);
      added = appendMedia(node, block) || added;
      if (block.caption && meaningful(block.caption)) {
        node.append(el('p', block.caption));
        added = true;
      }
      if (block.link && block.link_label) {
        const link = el('a', block.link_label);
        link.href = block.link;
        link.className = 'xuk-pdp-cta-link';
        node.append(link);
        added = true;
      }
      if (added) root.append(node);
    }

    const modules = [...root.querySelectorAll('[data-lower-module]')];
    if (modules.length) {
      const nav = el('nav');
      nav.className = 'xuk-pdp-tabs';
      nav.setAttribute('aria-label', data.navigationLabel || 'Product information');
      modules.forEach((module, index) => {
        const id = `xuk-pdp-${data.productId || 'product'}-${index}`;
        module.id = id;
        const anchor = el('a', module.querySelector('h2')?.textContent || 'Details');
        anchor.href = `#${id}`;
        nav.append(anchor);
      });
      root.prepend(nav);
    }

    if (meaningful(data.descriptionHtml)) {
      const node = section('description', data.descriptionHeading || 'Product details');
      const body = el('div');
      body.className = 'xlf-description';
      body.append(clean(data.descriptionHtml, media));
      body.querySelectorAll('p, div, span').forEach((child) => {
        if (!child.textContent.trim() && !child.querySelector('img, video')) child.remove();
      });
      if (body.textContent.trim() || body.querySelector('img, video')) {
        node.append(body);
        root.append(node);
      }
    }

    root.hidden = !root.children.length;
    root.dataset.productId = data.productId || '';
    root.dataset.ready = 'true';
    root.querySelectorAll('img').forEach((image) => {
      image.addEventListener(
        'error',
        () => {
          image.remove();
          root.querySelectorAll('[data-lower-module]').forEach((node) => {
            const content = node.textContent.replace(node.querySelector('h2')?.textContent || '', '').trim();
            if (!node.querySelector('img, video') && !content) node.remove();
          });
          root.hidden = !root.children.length;
        },
        { once: true }
      );
    });

    return root;
  }

  window.XukLowerDetails = { render };

  document.querySelectorAll('[data-xuk-lower]').forEach((root) => {
    const payload = root.querySelector('script[type="application/json"]');
    if (!payload) return;
    try {
      render(root, JSON.parse(payload.textContent));
    } catch (error) {
      root.hidden = true;
      console.error('Lower detail data invalid', error);
    }
  });
})();
